import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download,
  KeyRound,
  Loader2,
  MessageCircle,
  Phone,
  RefreshCw,
} from 'lucide-react';
import {
  SUPPORT_MOBILE,
  buildWhatsAppUrl,
  cachePortal,
  fetchCustomerPortal,
  getContractDownloadUrl,
  getRegisterCodeFromProfile,
  readCachedPortal,
  telSupportHref,
} from '../../services/managementService';

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return '—';
  return String(value).slice(0, 10);
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active' || s === 'paid') return 'bg-emerald-100 text-emerald-800';
  if (s === 'overdue' || s === 'expired' || s === 'cancelled') return 'bg-red-100 text-red-800';
  if (s === 'pending' || s === 'draft') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
}

function paymentStats(payments) {
  const rows = payments || [];
  const byStatus = (name) =>
    rows.filter((p) => String(p.Status || '').toLowerCase() === name);

  const paid = byStatus('paid');
  const pending = byStatus('pending');
  const overdue = byStatus('overdue');
  const other = rows.filter((p) => {
    const s = String(p.Status || '').toLowerCase();
    return s && s !== 'paid' && s !== 'pending' && s !== 'overdue';
  });

  const sum = (list) => list.reduce((acc, p) => acc + Number(p.Amount || 0), 0);

  return {
    total: rows.length,
    totalAmount: sum(rows),
    paidCount: paid.length,
    paidAmount: sum(paid),
    pendingCount: pending.length,
    pendingAmount: sum(pending),
    overdueCount: overdue.length,
    overdueAmount: sum(overdue),
    otherCount: other.length,
    otherAmount: sum(other),
  };
}

const TABS = [
  { id: 'contracts', label: 'Contracts' },
  { id: 'licenses', label: 'Licenses' },
  { id: 'payments', label: 'Payments' },
];

export default function LicenseInfo() {
  const [tab, setTab] = useState('contracts');
  const [portal, setPortal] = useState(() => readCachedPortal());
  const [loading, setLoading] = useState(!readCachedPortal());
  const [error, setError] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');

  const registerCode = useMemo(() => {
    try {
      return getRegisterCodeFromProfile(JSON.parse(localStorage.getItem('companyProfile') || '{}'));
    } catch {
      return '';
    }
  }, [portal]);

  const stats = useMemo(() => paymentStats(portal?.Payments), [portal]);

  const filteredPayments = useMemo(() => {
    const rows = portal?.Payments || [];
    if (paymentFilter === 'all') return rows;
    return rows.filter((p) => String(p.Status || '').toLowerCase() === paymentFilter);
  }, [portal, paymentFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let profile = {};
      try {
        profile = JSON.parse(localStorage.getItem('companyProfile') || '{}');
      } catch {
        profile = {};
      }
      const code = getRegisterCodeFromProfile(profile);
      if (!code) {
        setError('Register code is not set on company profile. Ask a super user (SA) to add it.');
        setPortal(null);
        return;
      }
      const data = await fetchCustomerPortal(code);
      if (!data.Found) {
        setError(data.Message || 'Customer not found for this register code.');
        setPortal(data);
        return;
      }
      cachePortal(data);
      setPortal(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not load license information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const waUrl = buildWhatsAppUrl({
    customerName: portal?.CustomerName,
    registerCode: portal?.Code || registerCode,
    reason: 'I need help with our Ainsoft Delivery license / contract.',
  });

  const openPayments = (filter) => {
    setPaymentFilter(filter);
    setTab('payments');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <KeyRound className="h-8 w-8 shrink-0 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">License info</h1>
              <p className="mt-1 text-sm text-gray-600">
                Contracts, licenses, and payments linked to your register code.
              </p>
              {portal?.CustomerName ? (
                <p className="mt-2 text-sm text-gray-700">
                  <span className="font-semibold">{portal.CustomerName}</span>
                  {portal.Code ? (
                    <span className="text-gray-500"> · {portal.Code}</span>
                  ) : null}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-medium text-white"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <a
              href={telSupportHref()}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white"
            >
              <Phone className="h-4 w-4" />
              {SUPPORT_MOBILE}
            </a>
          </div>
        </div>

        {portal?.HasExpiredLicense ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            License has expired. Please renew to stay current.
          </div>
        ) : null}
      </div>

      {/* Payment status counter cards */}
      {portal?.Found ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => openPayments('all')}
            className={`text-left rounded-xl border bg-white p-4 shadow-sm transition hover:shadow-md ${
              tab === 'payments' && paymentFilter === 'all'
                ? 'border-primary ring-2 ring-primary/20'
                : 'border-gray-200'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">All payments</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="mt-1 text-sm text-gray-600">{formatMoney(stats.totalAmount)}</p>
          </button>
          <button
            type="button"
            onClick={() => openPayments('paid')}
            className={`text-left rounded-xl border bg-emerald-50 p-4 shadow-sm transition hover:shadow-md ${
              tab === 'payments' && paymentFilter === 'paid'
                ? 'border-emerald-500 ring-2 ring-emerald-200'
                : 'border-emerald-200'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Paid</p>
            <p className="mt-1 text-2xl font-bold text-emerald-800">{stats.paidCount}</p>
            <p className="mt-1 text-sm text-emerald-700">{formatMoney(stats.paidAmount)}</p>
          </button>
          <button
            type="button"
            onClick={() => openPayments('pending')}
            className={`text-left rounded-xl border bg-amber-50 p-4 shadow-sm transition hover:shadow-md ${
              tab === 'payments' && paymentFilter === 'pending'
                ? 'border-amber-500 ring-2 ring-amber-200'
                : 'border-amber-200'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Pending</p>
            <p className="mt-1 text-2xl font-bold text-amber-800">{stats.pendingCount}</p>
            <p className="mt-1 text-sm text-amber-700">{formatMoney(stats.pendingAmount)}</p>
          </button>
          <button
            type="button"
            onClick={() => openPayments('overdue')}
            className={`text-left rounded-xl border bg-red-50 p-4 shadow-sm transition hover:shadow-md ${
              tab === 'payments' && paymentFilter === 'overdue'
                ? 'border-red-500 ring-2 ring-red-200'
                : 'border-red-200'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Overdue</p>
            <p className="mt-1 text-2xl font-bold text-red-800">{stats.overdueCount}</p>
            <p className="mt-1 text-sm text-red-700">{formatMoney(stats.overdueAmount)}</p>
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap gap-1 border-b border-gray-200 px-3 pt-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                if (t.id !== 'payments') setPaymentFilter('all');
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                tab === t.id
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-x-auto min-h-[200px]">
          {loading && !portal ? (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : tab === 'contracts' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Contract</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-left px-3 py-2">Period</th>
                  <th className="text-left px-3 py-2">Fee</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">File</th>
                </tr>
              </thead>
              <tbody>
                {(portal?.Contracts || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                      No contracts found.
                    </td>
                  </tr>
                ) : (
                  portal.Contracts.map((row) => {
                    const downloadUrl =
                      row.HasFile || row.FilePath
                        ? getContractDownloadUrl(portal.Code || registerCode, row.Id)
                        : '';
                    return (
                      <tr key={row.Id} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <div className="font-semibold text-gray-900">{row.ContractNo}</div>
                          <div className="text-xs text-gray-500">{row.Title}</div>
                        </td>
                        <td className="px-3 py-2">{row.ProductName || '—'}</td>
                        <td className="px-3 py-2">
                          {formatDate(row.StartDate)} → {formatDate(row.EndDate)}
                        </td>
                        <td className="px-3 py-2">
                          {formatMoney(row.LicenseFeeAmount)}
                          <div className="text-xs text-gray-500">{row.LicenseFeeType}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(row.Status)}`}>
                            {row.Status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {downloadUrl ? (
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            >
                              <Download className="h-3.5 w-3.5" />
                              Download
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">No file</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          ) : tab === 'licenses' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">License key</th>
                  <th className="text-left px-3 py-2">Contract</th>
                  <th className="text-left px-3 py-2">Issue</th>
                  <th className="text-left px-3 py-2">Expiry</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(portal?.Licenses || []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                      No licenses found.
                    </td>
                  </tr>
                ) : (
                  portal.Licenses.map((row) => (
                    <tr key={row.Id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono text-xs font-semibold break-all">
                        {row.LicenseKey}
                      </td>
                      <td className="px-3 py-2">{row.ContractNo || '—'}</td>
                      <td className="px-3 py-2">{formatDate(row.IssueDate)}</td>
                      <td className="px-3 py-2">
                        {formatDate(row.ExpiryDate)}
                        {row.IsExpired ? (
                          <div className="text-xs text-red-600 font-medium">Expired</div>
                        ) : (
                          <div className="text-xs text-gray-500">{row.DaysToExpiry}d left</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(row.Status)}`}>
                          {row.Status || '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <>
              {paymentFilter !== 'all' ? (
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-semibold capitalize">{paymentFilter}</span> payments
                  </p>
                  <button
                    type="button"
                    onClick={() => setPaymentFilter('all')}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Clear filter
                  </button>
                </div>
              ) : null}
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Fee</th>
                    <th className="text-left px-3 py-2">Contract</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Due</th>
                    <th className="text-left px-3 py-2">Paid</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                        No payments found.
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((row) => (
                      <tr key={row.Id} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <div className="font-medium">{row.FeeName || '—'}</div>
                          <div className="text-xs text-gray-500">{row.PaymentType}</div>
                        </td>
                        <td className="px-3 py-2">{row.ContractNo || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {formatMoney(row.Amount)}
                        </td>
                        <td className="px-3 py-2">
                          {formatDate(row.ExtendedDueDate || row.DueDate)}
                        </td>
                        <td className="px-3 py-2">{formatDate(row.PaidDate)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(row.Status)}`}>
                            {row.Status || '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
